import {
    FILTER_TYPE_SELECT,
    PRODUCT_COUNT_SMALL_WIDTH,
    PRODUCT_COUNT_FULL_WIDTH,
    FILTER_TYPE_CHECKBOX,
} from '../constant';
import { RECIPES_URL, getFetchRequest } from '../store/requests';
import { recipesService } from './recipesService';
import { isSmallWebScreen, sortByDate } from '../components/helper';
import { productsStorage } from './productsStorage';
import { productsService } from './productsService';
import { recipeIngredientService } from './recipeIngredientsService';
import { errorCallback, makeUrl } from '../util';
import {
    RECIPE_FILTER_GROUP_INGREDIENT,
    RECIPE_FILTER_GROUP_RECIPE_TYPE,
    RECIPE_FILTER_GROUP_SOURCE,
    RECIPE_FILTER_GROUP_TASTE,
} from './recipesUtil';
import { Filter, ItemsStorageService, SelectVariant } from '../shared/filtering/itemsStorageService';
import { Recipe, RecipeCharacteristicCategory, RecipeSource, RecipeTaste, RecipeType } from './recipeTypes';
import i18n from '../i18nService';

type I18nInstance = typeof i18n;

const createVariantsList = (productsList, i18nInstance: I18nInstance): SelectVariant[] => {
    return productsList.map((it) => ({
        id: it.productCode,
        name: productsService.getLocalization(i18nInstance.resolvedLanguage, it.productCode, true, i18nInstance),
    }));
};

const buildInitialFilters = (i18nInstance: I18nInstance): Filter[] => {
    return Object.values(RecipeType).map((it) => ({
        filterTitle: i18nInstance.t(`recipe.scheme.recipeType.${it.toLowerCase()}`),
        filterName: `${RECIPE_FILTER_GROUP_RECIPE_TYPE}.${it.toLowerCase()}`,
        filterType: FILTER_TYPE_CHECKBOX,
        variants: [],
        value: false,
        filterGroupName: RECIPE_FILTER_GROUP_RECIPE_TYPE,
    })).concat(Object.values(RecipeSource).map((it) => ({
        filterTitle: i18nInstance.t(`recipe.scheme.characteristic.${RECIPE_FILTER_GROUP_SOURCE}.${it}`),
        filterName: `${RECIPE_FILTER_GROUP_SOURCE}.${it.toLowerCase()}`,
        filterType: FILTER_TYPE_CHECKBOX,
        variants: [],
        value: false,
        filterGroupName: RECIPE_FILTER_GROUP_SOURCE,
    }))).concat(Object.values(RecipeTaste).map((it) => ({
        filterTitle: i18nInstance.t(`recipe.scheme.characteristic.${RECIPE_FILTER_GROUP_TASTE}.${it}`),
        filterName: `${RECIPE_FILTER_GROUP_TASTE}.${it.toLowerCase()}`,
        filterType: FILTER_TYPE_CHECKBOX,
        variants: [],
        value: false,
        filterGroupName: RECIPE_FILTER_GROUP_TASTE,
    })));
};

const createListOfFilters = (productsList, i18nInstance: I18nInstance): Filter[] => {
    return [{
        filterTitle: i18nInstance.t('app.page.property.filters.ingredient'),
        filterName: RECIPE_FILTER_GROUP_INGREDIENT,
        filterType: FILTER_TYPE_SELECT,
        variants: createVariantsList(productsList, i18nInstance),
        value: false,
        filterGroupName: RECIPE_FILTER_GROUP_INGREDIENT,
    }];
};

const filterRecipesListBySearchValue = (recipesList: Recipe[], searchValue: string, i18nInstance: I18nInstance) => {
    const normalizedSearch = searchValue.toLowerCase();
    return recipesList.filter((recipe) => {
        const recipeTypeLabel = recipesService.getLocalization(
            i18nInstance.resolvedLanguage,
            recipe.recipeType,
            true,
            i18nInstance,
        ).toLowerCase();
        const localizedNames = (recipe.names ?? [])
            .map((name) => name.name.toLowerCase())
            .join(' ');
        return recipeTypeLabel.includes(normalizedSearch) || localizedNames.includes(normalizedSearch);
    });
};

const parseFilterValue = (filterValue: string) => {
    if (filterValue === 'true') return true;
    if (filterValue === 'false') return false;
    return filterValue;
};

const filterRecipe = (recipe: Recipe, filters: Filter[]) => {
    if (filters.length === 0) return true;

    let matchedFilters = 0;
    for (const filter of filters) {
        if (filter.filterType === FILTER_TYPE_CHECKBOX) {
            const filterValue = parseFilterValue(filter.filterName.split('.')[1]);
            if (filter.filterGroupName === RECIPE_FILTER_GROUP_TASTE) {
                if (recipe.characteristics?.find(
                    (it) => it.category === RecipeCharacteristicCategory.RECIPE_CHARACTERISTIC_TASTE
                        && it.value === filterValue,
                )) {
                    matchedFilters++;
                }
                continue;
            }
            if (filter.filterGroupName === RECIPE_FILTER_GROUP_SOURCE) {
                if (recipe.characteristics?.find(
                    (it) => it.category === RecipeCharacteristicCategory.RECIPE_CHARACTERISTIC_SOURCE
                        && it.value === filterValue,
                )) {
                    matchedFilters++;
                }
                continue;
            }
            if (filter.filterGroupName === RECIPE_FILTER_GROUP_RECIPE_TYPE
                && recipe.recipeType?.toLowerCase() === filterValue) {
                matchedFilters++;
            }
        } else if (filter.filterGroupName === RECIPE_FILTER_GROUP_INGREDIENT) {
            const currentProduct = productsStorage.getById(filter.id);
            if (currentProduct && recipe.ingredients?.find(
                (ingredient) => ingredient.ingredientParts?.find(
                    (ingredientPart) => ingredientPart.productCode === currentProduct.productCode,
                ),
            )) {
                matchedFilters++;
            }
        }
    }
    return matchedFilters === filters.length;
};

interface RecipesStorage {
    loadItems(callback: Function, width: number): void;
    loadFilters(i18nInstance?: I18nInstance, callback?: Function): void;
    getMaxCount(): number;
    getCurrentPage(): number;
    setPageNumber(page: number): void;
    getItems(loadAllData?: boolean): Recipe[];
}

class RecipesStorageImpl implements ItemsStorageService, RecipesStorage {
    recipesList: Recipe[] = [];
    searchValue = '';
    filtersObserversList: Function[] = [];
    possibleFilters: Filter[] = [];
    filtersList: Filter[] = [];
    page = 1;
    offset = 10;
    maxPages = 1;
    private i18nInstance: I18nInstance = i18n;

    loadFilters(i18nInstance: I18nInstance = i18n, callback?: Function) {
        this.i18nInstance = i18nInstance;
        this.possibleFilters = [
            ...buildInitialFilters(i18nInstance),
            ...createListOfFilters(productsStorage.getItems(true), i18nInstance),
        ];
        callback?.();
    }

    loadItems(callback?: Function, width?: number) {
        getFetchRequest(makeUrl([RECIPES_URL, '?withProducts=true']), (response) => {
            const recipes = (response ?? []).sort(sortByDate);
            this.recipesList = recipes;
            this.offset = isSmallWebScreen(width) ? PRODUCT_COUNT_SMALL_WIDTH : PRODUCT_COUNT_FULL_WIDTH;
            this.reloadPagesNumber();
            callback?.(response);
            this.notifyFiltersChangingObservers();
        }, errorCallback);
    }

    getMaxCount() {
        return this.maxPages;
    }

    getCurrentPage() {
        return this.page;
    }

    setPageNumber(value: number) {
        this.page = value;
        this.notifyFiltersChangingObservers();
    }

    getItems(loadAllData?: boolean): Recipe[] {
        let filteredList = [...this.recipesList];
        if (this.searchValue) {
            filteredList = filterRecipesListBySearchValue(filteredList, this.searchValue, this.i18nInstance);
        }
        if (this.filtersList.length) {
            filteredList = filteredList.filter((recipe) => filterRecipe(recipe, this.filtersList));
        }
        this.maxPages = Math.max(1, Math.ceil(filteredList.length / this.offset));
        return loadAllData ? filteredList : filteredList.slice((this.page - 1) * this.offset, this.page * this.offset);
    }

    setInitialFilters(searchParams, callback: Function) {
        if (!this.possibleFilters.length) {
            this.loadFilters(this.i18nInstance, () => this.applyInitialFilters(searchParams, callback));
            return;
        }
        this.applyInitialFilters(searchParams, callback);
    }

    private applyInitialFilters(searchParams, callback: Function) {
        const filtersList: Filter[] = [];
        searchParams.forEach((value, key) => {
            const currentFilter = this.possibleFilters.find((it) => it.filterName === key);
            if (currentFilter?.filterType === FILTER_TYPE_CHECKBOX) {
                currentFilter.value = parseFilterValue(value);
                filtersList.push(currentFilter);
            }
            if (currentFilter?.filterType === FILTER_TYPE_SELECT) {
                value.split(',').forEach((it) => {
                    filtersList.push({ value, filterType: FILTER_TYPE_SELECT, id: it });
                    recipeIngredientService.addItem(it);
                });
            }
        });
        this.filtersList = filtersList;
        callback?.();
        this.notifyFiltersChangingObservers();
    }

    getPossibleFilters() {
        if (!this.possibleFilters.length) {
            this.loadFilters(this.i18nInstance);
        }
        return this.possibleFilters;
    }

    getCurrentFilters() {
        return this.filtersList;
    }

    getAnyFiltersByGroup(groupName: string) {
        return this.possibleFilters.find((it: Filter) => it.filterGroupName === groupName);
    }

    getCurrentFiltersByGroup(groupName) {
        return this.possibleFilters.filter((it) => it.filterGroupName === groupName);
    }

    getFilter(filterName) {
        return this.possibleFilters.find((it) => it.filterName === filterName);
    }

    setSearchString(searchStringValue: string) {
        this.searchValue = searchStringValue;
        this.page = 1;
        this.notifyFiltersChangingObservers();
    }

    getSearchString() {
        return this.searchValue;
    }

    setSelectedFilterValue(filterName: string, selectedValue: string) {
        const currentFilter = this.getFilter(filterName);
        if (currentFilter?.filterType === FILTER_TYPE_SELECT) {
            selectedValue.split(',').forEach((it) => {
                if (!it) {
                    return;
                }
                if (this.filtersList.find((filter) => filter.id === it && filter.filterType === FILTER_TYPE_SELECT)) {
                    this.filtersList = this.filtersList.filter(
                        (filter) => !(filter.filterType === FILTER_TYPE_SELECT && filter.id === it),
                    );
                } else {
                    this.filtersList.push({
                        filterType: FILTER_TYPE_SELECT,
                        id: it,
                        filterGroupName: RECIPE_FILTER_GROUP_INGREDIENT,
                        filterName: RECIPE_FILTER_GROUP_INGREDIENT,
                        filterTitle: '',
                    });
                }
            });
            this.maxPages = 1;
            this.page = 1;
        }
    }

    setCheckedFilterValue(filterName: string, selectedValue: boolean) {
        const currentFilter = this.getFilter(filterName);
        if (currentFilter?.filterType === FILTER_TYPE_CHECKBOX) {
            if (!selectedValue) {
                this.filtersList = this.filtersList.filter(
                    (filter) => !(filter.filterType === FILTER_TYPE_CHECKBOX && filter.filterName === filterName),
                );
            } else {
                currentFilter.value = true;
                this.filtersList.push(currentFilter);
            }
        }
    }

    setNumberFilterValue(_filterName: string, _selectedValue: number) {
    }

    cleanFilterValues() {
        this.filtersList = [];
        this.possibleFilters.forEach((it) => {
            it.value = false;
        });
        this.reloadPagesNumber();
        this.notifyFiltersChangingObservers();
        recipeIngredientService.deleteItem('', true);
    }

    registerFiltersChangingObserver(observer: Function) {
        this.filtersObserversList.push(observer);
    }

    unRegisterFiltersChangingObserver(observer: Function) {
        this.filtersObserversList = this.filtersObserversList.filter((observerItem) => observerItem !== observer);
    }

    notifyFiltersChangingObservers() {
        this.filtersObserversList.forEach((observer: Function) => observer());
    }

    reloadPagesNumber() {
        this.maxPages = Math.max(1, Math.ceil(this.getItems(true).length / this.offset));
    }

    changeDeletedItemsView() {
    }

    changeValidItemsView() {
    }
}

export const recipesStorageInstance = new RecipesStorageImpl();
